import {useRef, type ReactNode} from 'react';
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from '@headlessui/react';
import {XMarkIcon} from '@heroicons/react/24/outline';
import {PrimaryButton} from './PrimaryButton.tsx';
import {SecondaryButton} from './SecondaryButton.tsx';

interface FormModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: () => void;
    title: string;
    subtitle?: string;
    submitLabel?: string;
    cancelLabel?: string;
    isSubmitting?: boolean;
    leftFooterContent?: ReactNode;
    width?: 'lg' | 'xl' | '2xl' | '3xl';
    children: ReactNode;
}

const maxWidths: Record<NonNullable<FormModalProps['width']>, string> = {
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
};

export default function FormModal({
                                      open,
                                      onClose,
                                      onSubmit,
                                      title,
                                      subtitle,
                                      submitLabel = 'Opslaan',
                                      cancelLabel = 'Annuleren',
                                      isSubmitting = false,
                                      leftFooterContent,
                                      width = '2xl',
                                      children,
                                  }: FormModalProps) {
    const formRef = useRef<HTMLFormElement>(null);

    function requestSubmit() {
        formRef.current?.requestSubmit();
    }

    return (
        <Dialog open={open} onClose={onClose} className="relative z-50">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-stretch justify-center p-0 sm:items-center sm:p-4">
                    <DialogPanel
                        transition
                        className={`relative flex h-full max-h-full w-full ${maxWidths[width]} transform flex-col overflow-hidden bg-white shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:h-auto sm:max-h-[85vh] sm:rounded-xl data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
                    >
                        <div className="flex-shrink-0 px-8 pt-7">
                            <div className="flex items-start justify-between gap-4">
                                <DialogTitle as="h2" className="text-xl font-semibold text-gray-900">
                                    {title}
                                </DialogTitle>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label="Sluiten"
                                    className="mt-0.5 rounded-md text-gray-400 transition hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                >
                                    <XMarkIcon aria-hidden="true" className="h-6 w-6"/>
                                </button>
                            </div>
                            {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
                        </div>

                        <form
                            ref={formRef}
                            className="flex min-h-0 flex-1 flex-col"
                            onSubmit={(event) => {
                                event.preventDefault()
                                if (!isSubmitting) onSubmit()
                            }}
                        >
                            <div className="flex-1 overflow-y-auto px-8 pb-2 pt-6">
                                {children}
                            </div>

                            <div className="flex flex-shrink-0 items-center justify-between gap-3 px-8 py-5">
                                <div>{leftFooterContent}</div>
                                <div className="flex items-center gap-3">
                                    <SecondaryButton title={cancelLabel} onClick={onClose}/>
                                    <PrimaryButton
                                        title={isSubmitting ? 'Bezig met opslaan...' : submitLabel}
                                        onClick={requestSubmit}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>
                        </form>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
